/**
 * Admin Licenses API Service
 * Handles license management operations
 */

import { get } from "../client";
import { AppLicense } from "../../../types";

// === Response Types ===

interface ApiLicense {
  id: string;
  domain: string;
  start_date: string;
  end_date: string;
  free_period_months: number;
  free_period_end: string;
  service_fee_percent: number;
  status: "active" | "expired" | "suspended";
  remaining_days: number;
  is_in_free_period: boolean;
  created_at: string;
  updated_at: string;
}

interface LicensesResponse {
  success: boolean;
  data: ApiLicense[];
}

interface LicenseResponse {
  success: boolean;
  data: ApiLicense;
  message?: string;
}

interface LicenseStatsResponse {
  success: boolean;
  data: {
    totalFee: number;
    freeTxCount: number;
    billableTxCount: number;
    totalTx: number;
    serviceFeePercent: number;
  };
}

// === Transform Functions ===

function transformLicense(apiLicense: ApiLicense): AppLicense {
  const statusMap: Record<string, AppLicense["status"]> = {
    active: "Active",
    expired: "Expired",
    suspended: "Grace Period",
  };

  return {
    id: apiLicense.id,
    clientName: apiLicense.domain,
    startDate: apiLicense.start_date,
    endDate: apiLicense.end_date,
    status: statusMap[apiLicense.status] || "Active",
    feePerTransaction: apiLicense.service_fee_percent / 100,
    freePeriodMonths: apiLicense.free_period_months,
    freePeriodEnd: apiLicense.free_period_end,
    remainingDays: apiLicense.remaining_days,
    isInFreePeriod: apiLicense.is_in_free_period,
  };
}

// === API Functions ===

/**
 * Get all licenses
 */
export async function getLicenses(): Promise<AppLicense[]> {
  const response = await get<LicensesResponse>("/admin/licenses");
  return response.data.map(transformLicense);
}

/**
 * Get single license by ID
 */
export async function getLicense(id: string): Promise<AppLicense> {
  const response = await get<LicenseResponse>(`/admin/licenses/${id}`);
  return transformLicense(response.data);
}

/**
 * Get license statistics
 */
export async function getLicenseStats(): Promise<{
  totalFee: number;
  freeTxCount: number;
  billableTxCount: number;
  totalTx: number;
  serviceFeePercent: number;
}> {
  const response = await get<LicenseStatsResponse>("/admin/licenses/stats");
  return response.data;
}
